import { AdsCountType, VideoAdsDetails } from 'src/components/AdSystem/types/ad.types';
import { LocalStorageKeys } from 'src/constants/constants';
import { getRandomIdCookies, getTokenCookies } from 'src/lib/cookie-service';
import { parsePlayerAuthParams } from 'src/playerParams';
import { PlayerAuthParamsState } from 'src/types/playerParams.type';

import { logger } from '../logger/loggerService';
import PwWebSDKInstance from '../pwWebSDKService';
import { isWEBSDKSupported } from '../utils';

class LocalStorageService {
  private static instance: LocalStorageService;
  private readonly VIDEO_ADS_COUNT_KEY = LocalStorageKeys.AD_VIEW_KEY;
  private readonly VIDEO_ADS_DETAILS_KEY = LocalStorageKeys.VIDEO_ADS_DETAILS;
  private authParams: Partial<PlayerAuthParamsState> | null = null;

  private constructor() {
    this.initAuthParams();
  }

  private _safeParse<T = any>(raw: string, key: LocalStorageKeys): T | null {
    try {
      return raw ? (JSON.parse(raw) as T) : null;
    } catch (e) {
      logger.error('LocalStorage parse error', { data: raw, error: e, key });
      return null;
    }
  }

  public static getInstance(): LocalStorageService {
    if (!LocalStorageService.instance) {
      LocalStorageService.instance = new LocalStorageService();
    }
    return LocalStorageService.instance;
  }

  private initAuthParams() {
    try {
      this.authParams = parsePlayerAuthParams();
    } catch (e) {
      logger.error('Error parsing auth params', e);
      this.authParams = {};
    }
  }

  // Optional: method to refresh params if URL changes
  public refreshAuthParams() {
    this.initAuthParams();
  }

  setItem(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      logger.error('Error setting localStorage item', key, value, e);
    }
  }

  getItem(key: string): string | string {
    try {
      return localStorage.getItem(key) || '';
    } catch (error) {
      logger.error('Error getting localStorage item', { key }, { error });
      return '';
    }
  }

  removeItem(key: string) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      logger.error('Error removing localStorage item', { key }, { error: e });
    }
  }

  getVideoAdsDetails(): VideoAdsDetails | null {
    const adsDetails = JSON.parse(
      this.getItem(this.VIDEO_ADS_DETAILS_KEY) || '{}',
    ) as VideoAdsDetails;
    return adsDetails?._id ? adsDetails : null;
  }

  getVideoAdsViews(): number {
    const date: string = new Date().toDateString();
    const raw = this.getItem(this.VIDEO_ADS_COUNT_KEY);
    const stats = this._safeParse<AdsCountType>(raw, this.VIDEO_ADS_COUNT_KEY) || {};
    return Number(stats[date] ?? 0);
  }

  incrementVideoAdsCount(): number {
    const date: string = new Date().toDateString();
    const raw = this.getItem(this.VIDEO_ADS_COUNT_KEY);
    const stats: AdsCountType = this._safeParse<AdsCountType>(raw, this.VIDEO_ADS_COUNT_KEY) || {};

    const current = Number(stats[date] ?? 0) + 1;

    stats[date] = current;

    this.setItem(this.VIDEO_ADS_COUNT_KEY, JSON.stringify(stats));
    return current;
  }

  getToken(): string {
    if (isWEBSDKSupported() && PwWebSDKInstance?.isLoggedIn) {
      return PwWebSDKInstance?.tokenContext?.token || '';
    }

    return (
      this.authParams?.token || getTokenCookies() || this.getItem(LocalStorageKeys.TOKEN_CAPS) || ''
    );
  }

  getRandomId(): string {
    if (isWEBSDKSupported() && PwWebSDKInstance?.isLoggedIn) {
      return PwWebSDKInstance?.randomId || '';
    }

    return (
      this.authParams?.randomId ||
      getRandomIdCookies() ||
      this.getItem(LocalStorageKeys.RANDOMID) ||
      this.getItem(LocalStorageKeys.UUID) ||
      ''
    );
  }

  getUser(): any {
    const user = this.getItem(LocalStorageKeys.USER);
    try {
      return user ? JSON.parse(user) : null;
    } catch (e) {
      logger.error('Error parsing user from localStorage', user, e);
      return null;
    }
  }

  setUser(user: any) {
    this.setItem(LocalStorageKeys.USER, JSON.stringify(user));
  }

  setNotesEnabledBatchIds(value: string) {
    return this.setItem(LocalStorageKeys.NOTES_ENABLED_BATCH_IDS, value);
  }

  getNotesEnabledBatchIds() {
    return this.getItem(LocalStorageKeys.NOTES_ENABLED_BATCH_IDS);
  }
  safeParse<T>(value: string): T | null {
    try {
      return value ? (JSON.parse(localStorage.getItem(value) || '{}') as T) : null;
    } catch (e) {
      logger.error('Error parsing value from localStorage', value, e);
      return null;
    }
  }
}

// Export the singleton instance
export const LocalStorageInstance = LocalStorageService.getInstance();
